// src/locales/en/shops.js
export const shops = {
    // Navigation
    createNewShop: "Create New Shop",
    setupYourShop: "Set up your shop details and configuration",
    backToShops: "Back to Shops",
    
    // Shop Types
    online: "Online Shop",
    pos: "POS/Physical Store",
    dropshipper: "Dropshipper",
    
    // Form Fields
    basicInformation: "Basic Information",
    basicInformationDesc: "Provide basic details about your shop",
    shopName: "Shop Name",
    shopNamePlaceholder: "Enter your shop name",
    printableName: "Printable Name",
    printableNamePlaceholder: "Name to print on waybills (optional)",
    printableNameHint: "If set, this name is printed on waybill labels instead of the shop name",
    shopType: "Shop Type",
    orderPrefix: "Order Number Prefix",
    prefixHint: "This will be used to generate order numbers (e.g., ABC-001)",
    addressPlaceholder: "Enter shop address",
    phone: "Phone Number",
    phonePlaceholder: "Enter contact number",
    address: "Address",
    
    // Logo Section
    shopLogo: "Shop Logo",
    logoDesc: "Upload your shop logo (optional)",
    uploadLogo: "Click to upload logo",
    logoFormats: "PNG, JPG, WEBP up to 5MB",
    
    // Delivery Charges
    deliveryCharges: "Delivery Charges",
    deliveryChargesDesc: "Set delivery charges for each shipping zone",
    deliveryChargesHint: "Set competitive delivery charges for each zone. These rates will be charged from customers.",
    
    // Service Charge
    serviceCharge: "Service Charge",
    serviceChargeDesc: "Fixed service charge amount for dropshipper orders",
    serviceChargeAmount: "Service Charge Amount (LKR)",
    serviceChargeHint: "Fixed amount charged per order",
    
    // Validation Messages
    nameRequired: "Shop name is required",
    typeRequired: "Please select a shop type",
    prefixRequired: "Order prefix is required",
    prefixMinLength: "Prefix must be at least 2 characters",
    prefixTaken: "This prefix is already taken",
    prefixAvailable: "Prefix is available",
    invalidPhone: "Please enter a valid phone number",
    deliveryChargesRequired: "Please set delivery charges for all zones",
    serviceChargeRequired: "Service charge is required for dropshippers",
    invalidServiceCharge: "Please enter a valid service charge",
    invalidLogoFormat: "Logo must be PNG, JPG, JPEG, or WEBP format",
    logoTooLarge: "Logo file size must be less than 5MB",
    
    // Loading States
    loadingData: "Loading Data...",
    loadingDataDesc: "Please wait while we fetch the required information.",
    
    // Actions
    createShop: "Create Shop",
    
    // Success/Error Messages
    shopCreatedSuccess: "Shop '{{shopName}}' created successfully!",
    failedToCreate: "Failed to create shop. Please try again.",
    failedToLoadZones: "Failed to load shipping zones. Please try again.",

    // Shops List Page
    manageYourShops: "Manage all your shops and their configurations",
    loadingShops: "Loading Shops...",
    loadingShopsDesc: "Please wait while we fetch your shops.",
    searchShops: "Search shops by name, prefix, address, or phone...",
    filteredResults: "{{count}} of {{total}} shops",
    totalShops: "{{count}} shops",
    allShops: "All Shops",
    noSearchResults: "No shops found",
    noSearchResultsDesc: "Try adjusting your search terms or clear filters to see all shops.",
    noShops: "No shops yet",
    noShopsDesc: "Create your first shop to start managing your business operations.",
    createFirstShop: "Create First Shop",

    // Edit Shop
    editShop: "Edit Shop",
    updateShop: "Update Shop",
    updateShopDetails: "Update your shop details and configuration",
    shopUpdatedSuccess: "Shop '{{shopName}}' updated successfully!",
    failedToUpdate: "Failed to update shop. Please try again.",
    unsavedChanges: "Unsaved Changes",
    confirmDiscardChanges: "You have unsaved changes. Are you sure you want to leave without saving?",
    discardChanges: "Discard Changes",
    noChangesToSave: "No changes have been made to save.",
    noChanges: "No Changes",

    // Table Headers
    logo: "Logo",
    type: "Type",
    prefix: "Prefix",
    contact: "Contact",
    
    // Table Content
    noContact: "No contact info",
    
    // Actions
    confirmDelete: "Are you sure you want to delete the shop '{{shopName}}'? This action cannot be undone.",
    shopDeletedSuccess: "Shop '{{shopName}}' has been deleted successfully.",
    failedToDelete: "Failed to delete shop. Please try again.",

    // Bulk Edit
    bulkEdit: "Bulk Edit",
    setFreeDeliveryAll: "Free Delivery for All",
    bulkEditDeliveryCharges: "Bulk Edit Delivery Charges",
    deliveryChargeAmount: "Delivery Charge Amount",
    bulkEditHint: "This amount will be applied to all shipping zones",
    applyToAll: "Apply to All",
    invalidBulkEditValue: "Please enter a valid delivery charge amount",
    bulkEditApplied: "Delivery charges updated for all zones",
    freeDeliveryApplied: "Free delivery set for all zones",

    // Extra Weight Charges
    setupExtraCharges: "Setup extra charges for additional weight",
    extraChargesDesc: "Charge customers per extra kilogram beyond the base delivery",
    extraChargePerKg: "Extra Charge per KG",
    extraChargePerKgPlaceholder: "0.00",
    extraChargesRequired: "Please enter valid extra charges per KG for all zones",

    // Catalog Settings
    catalogSettings: "Public Catalog",
    catalogSettingsDesc: "Let customers browse products and place orders through a public catalog link",
    catalogEnabled: "Enable Public Catalog",
    catalogEnabledHint: "When enabled, customers can browse your products and place orders at your catalog URL",
    catalogSlug: "Catalog URL",
    catalogSlugPlaceholder: "e.g. my-shop",
    catalogSlugHint: "3–80 characters, lowercase letters, numbers, and hyphens only",
    catalogSlugAvailable: "Slug is available",
    catalogSlugTaken: "This slug is already taken",
    catalogSlugInvalid: "Invalid slug format",
    catalogSlugRequired: "A catalog URL is required to enable the catalog",
    generateSlug: "Generate from name",

    // Message Center Shift Settings
    messageCenterShiftSettings: "Message Center Shifts",
    messageCenterShiftSettingsDesc: "Control whether orders for this shop can be tagged with a message center shift",
    messageCenterShiftEligible: "Enable Message Center Shifts for this shop",
    messageCenterShiftEligibleHint: "When enabled, orders for this shop can be assigned to a message center shift",
    catalogPreviewLink: "Preview catalog",
    catalogUrlLabel: "Your catalog URL will be:",

    // POS Settings
    posSettings: "POS Settings",
    posSettingsDesc: "Configure point-of-sale behaviour for this shop",
    allowCreditSales: "Allow Credit Sales",
    allowCreditSalesHint: "Allow cashiers to complete orders without full payment (credit/tab)",
    cashierSessionRequired: "Require Cashier Session",
    cashierSessionRequiredHint: "Cashiers must open a session before using the POS terminal",

    // WooCommerce Webhook Integration
    woocommerceIntegration: "WooCommerce Integration",
    woocommerceIntegrationDesc: "Automatically create orders in Rettzy when they are placed on your WooCommerce store",
    woocommerceWebhookEnabled: "Enable WooCommerce Webhook",
    woocommerceWebhookEnabledHint: "When enabled, WooCommerce can send new orders and payment updates directly to Rettzy",
    woocommerceWebhookEnabledSuccess: "WooCommerce webhook enabled",
    woocommerceWebhookDisabledSuccess: "WooCommerce webhook disabled",
    woocommerceWebhookToggleFailed: "Failed to update WooCommerce webhook status. Please try again.",
    woocommerceWebhookSecret: "Webhook Secret",
    woocommerceWebhookSecretHint: "Paste this into the Secret field on both webhook topics in WooCommerce",
    woocommerceOrderCreatedUrl: "Order Created Webhook URL",
    woocommerceOrderStatusUpdatedUrl: "Order Status Updated Webhook URL",
    woocommerceRegenerateSecret: "Regenerate Secret",
    woocommerceRegenerateSecretConfirmTitle: "Regenerate Webhook Secret",
    woocommerceRegenerateSecretConfirm: "This will invalidate the current secret. You'll need to update the Secret field on both webhook topics in WooCommerce with the new value.",
    woocommerceRegenerateSecretSuccess: "Webhook secret regenerated. Update it in WooCommerce.",
    woocommerceRegenerateSecretFailed: "Failed to regenerate webhook secret. Please try again.",
    woocommerceShowSecret: "Show secret",
    woocommerceHideSecret: "Hide secret",
    woocommerceCopy: "Copy",
    woocommerceCopied: "Copied to clipboard",
    woocommerceCopyFailed: "Failed to copy. Please copy it manually.",
    woocommerceLastReceivedAt: "Last webhook received: {{date}}",
    woocommerceNeverReceived: "No webhook received yet",
    woocommerceSetupHint: "In WooCommerce, go to Settings → Advanced → Webhooks and create one webhook per URL below (topics: Order created / Order updated), pasting in the matching URL and the Secret.",
    woocommerceApiTitle: "WooCommerce REST API (manual order sync)",
    woocommerceApiDesc: "Lets Rettzy fetch orders that a missed webhook never delivered. Create a Read-only API key in WooCommerce → Settings → Advanced → REST API.",
    woocommerceStoreUrl: "Store URL",
    woocommerceStoreUrlPlaceholder: "https://your-store.com",
    woocommerceConsumerKey: "Consumer Key",
    woocommerceConsumerSecret: "Consumer Secret",
    woocommerceKeySaved: "Saved: {{key}}. Leave blank to keep the stored key.",
    woocommerceSecretSaved: "•••••••• saved — leave blank to keep",
    woocommerceSaveApi: "Save API credentials",
    woocommerceTestConnection: "Test connection",
    woocommerceApiSaveSuccess: "WooCommerce API credentials saved",
    woocommerceApiSaveFailed: "Failed to save WooCommerce API credentials",
    woocommerceTestSuccess: "Connection to WooCommerce successful",
    woocommerceTestFailed: "Could not connect to WooCommerce",
    woocommerceSaveCredentialsFirst: "Save the API credentials before testing the connection",
    woocommerceSyncTitle: "WooCommerce Order Sync",
    woocommerceSyncDesc: "Pull orders that a missed webhook never delivered, directly from WooCommerce.",
    woocommerceSyncSelectShop: "Shop",
    woocommerceSyncChooseShop: "Select a shop",
    woocommerceSyncNoShops: "No shops have the WooCommerce webhook enabled. Enable it in Edit Shop first.",
    woocommerceSyncMissingCredentials: "This shop has no WooCommerce REST API keys yet.",
    woocommerceSyncAddCredentials: "Add API keys in Edit Shop",
    woocommerceSyncLastSync: "Last manual sync: {{date}}",
    woocommerceSyncNeverSynced: "No manual sync yet",
    woocommerceSyncByIdTitle: "Sync one order",
    woocommerceSyncByIdDesc: "Enter a WooCommerce order id. The order is fetched and created in Rettzy, or updated if it already exists.",
    woocommerceSyncOrderIdLabel: "WooCommerce order id",
    woocommerceSyncFetch: "Fetch & Sync",
    woocommerceSyncByDateTitle: "Sync orders by date",
    woocommerceSyncByDateDesc: "Fetch every WooCommerce order created on the chosen date (up to 500) and create or update them in Rettzy.",
    woocommerceSyncDateLabel: "Order date",
    woocommerceSyncOrders: "Sync orders",
    woocommerceSyncing: "Syncing…",
    woocommerceSyncingHint: "This may take a minute. Please keep this page open.",
    woocommerceSyncConfirmTitle: "Sync WooCommerce orders",
    woocommerceSyncConfirm: "Fetch all WooCommerce orders from {{shop}} created on {{date}} and create or update them in Rettzy?",
    woocommerceSyncResults: "Sync results",
    woocommerceSyncTotal: "Total",
    woocommerceSyncCreated: "Created",
    woocommerceSyncUpdated: "Updated",
    woocommerceSyncSkipped: "Skipped",
    woocommerceSyncFailed: "Failed",
    woocommerceSyncColWcId: "WooCommerce order",
    woocommerceSyncColStatus: "Result",
    woocommerceSyncColOrder: "Rettzy order",
    woocommerceSyncColMessage: "Details",
    woocommerceSyncNoOrders: "No WooCommerce orders found for that date.",
    woocommerceSyncCopyFailed: "Copy failed order ids",
    woocommerceSyncError: "WooCommerce sync failed",
    woocommerceSyncInvalidOrderId: "Enter a valid WooCommerce order id",
    woocommerceSyncLoadFailed: "Failed to load shops",

    // Bundle products
    bundleConfigure: "Configure Bundle Pricing",
    bundleComponentsMissing: "These bundle products are not assigned to this shop: {products}",
    bundlePrice: "Bundle Price",
    unitValue: "Unit Value",
    lineDiscount: "Discount",
    bundleLoadFailed: "Failed to load bundle details",
}